'use client';

import React, { Fragment, useMemo } from 'react';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { Plus } from 'assets/icons';
import type { LegalDoc, LegalBlock, RichRun } from 'content/legal/types';
import {
  PolicyPage,
  PolicyContainer,
  Eyebrow,
  PageTitle,
  Divider,
} from 'components/shared/policy-layout/PolicyLayout';
import { BackToTop } from './BackToTop';
import styles from './LegalDocument.module.css';

// ---------------------------------------------------------------------------
// Section model — heading ids + table-of-contents, derived at render time
// ---------------------------------------------------------------------------

type Section = { id: string; title: string; level: 1 | 2 };

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const headingText = (runs: RichRun[]): string =>
  runs.map((r) => r.text).join('');

/**
 * Single pass over the top-level blocks: assigns a stable, unique `id` to every
 * heading (keyed by block reference) and collects the level-1/2 headings for the
 * "jump to section" table of contents. Derived from content — nothing is stored
 * on the LegalDoc, so the content modules stay untouched.
 */
function collectSections(blocks: LegalBlock[]): {
  sections: Section[];
  headingIds: Map<LegalBlock, string>;
} {
  const sections: Section[] = [];
  const headingIds = new Map<LegalBlock, string>();
  const used = new Map<string, number>();

  for (const block of blocks) {
    if (block.kind !== 'heading') continue;
    const title = headingText(block.runs).trim();
    const base = slugify(title) || 'section';
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    const id = seen === 0 ? base : `${base}-${seen + 1}`;
    headingIds.set(block, id);
    if (block.level === 1 || block.level === 2) {
      sections.push({ id, title, level: block.level });
    }
  }

  return { sections, headingIds };
}

// ---------------------------------------------------------------------------
// Rich-text inline renderer
// ---------------------------------------------------------------------------

function renderRun(run: RichRun, index: number): React.ReactNode {
  let node: React.ReactNode = run.text;

  if (run.bold) node = <strong key={`b-${index}`}>{node}</strong>;
  if (run.italic) node = <em key={`i-${index}`}>{node}</em>;
  if (run.href)
    node = (
      <a
        key={`a-${index}`}
        href={run.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {node}
      </a>
    );

  return <Fragment key={index}>{node}</Fragment>;
}

function renderRuns(runs: RichRun[]): React.ReactNode {
  return runs.map((run, i) => renderRun(run, i));
}

// ---------------------------------------------------------------------------
// Block renderer (recursive for toggle children)
// ---------------------------------------------------------------------------

type GroupedBlock =
  | { type: 'single'; block: LegalBlock }
  | { type: 'listGroup'; items: LegalBlock[] };

/** Coalesce consecutive listItem blocks into a single ordered-list group. */
function groupBlocks(blocks: LegalBlock[]): GroupedBlock[] {
  const result: GroupedBlock[] = [];
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i];

    if (block.kind === 'listItem') {
      const items: LegalBlock[] = [];
      while (i < blocks.length) {
        if (blocks[i].kind === 'listItem') {
          items.push(blocks[i]);
          i++;
        } else if (
          blocks[i].kind === 'divider' &&
          blocks[i + 1]?.kind === 'listItem'
        ) {
          // Notion exports sprinkle dividers between numbered items; absorb them
          // so the list renders as one continuous <ol> (1, 2, 3…) rather than a
          // series of single-item lists that each reset to "1." (and drop the
          // stray rules between items).
          i++;
        } else {
          break;
        }
      }
      result.push({ type: 'listGroup', items });
    } else {
      result.push({ type: 'single', block });
      i++;
    }
  }

  return result;
}

function renderBlock(
  block: LegalBlock,
  index: number,
  headingIds: Map<LegalBlock, string>,
): React.ReactNode {
  switch (block.kind) {
    case 'heading': {
      const id = headingIds.get(block);
      if (block.level === 1)
        return (
          <h1 key={index} id={id} className={styles.h1}>
            {renderRuns(block.runs)}
          </h1>
        );
      if (block.level === 2)
        return (
          <h2 key={index} id={id} className={styles.h2}>
            {renderRuns(block.runs)}
          </h2>
        );
      return (
        <h3 key={index} id={id} className={styles.h3}>
          {renderRuns(block.runs)}
        </h3>
      );
    }

    case 'paragraph':
      return (
        <p key={index} className={styles.p}>
          {renderRuns(block.runs)}
        </p>
      );

    case 'callout':
      return (
        <div key={index} role="note" className={styles.callout}>
          {renderRuns(block.runs)}
        </div>
      );

    case 'quote':
      return (
        <blockquote key={index} className={styles.blockquote}>
          {renderRuns(block.runs)}
        </blockquote>
      );

    case 'toggle':
      return (
        <details key={index} className={styles.details}>
          <summary className={styles.summary}>
            <span>{renderRuns(block.summary)}</span>
            <span aria-hidden="true" className={styles.toggleIcon}>
              <Plus fill="#A1A1A1" style={{ width: 16, height: 16 }} />
            </span>
          </summary>
          <div className={styles.toggleBody}>
            {renderBlocks(block.children, headingIds)}
          </div>
        </details>
      );

    case 'table': {
      // Some source tables have no extractable cell content (empty rows);
      // nothing to render in that case.
      if (!block.rows || block.rows.length === 0) return null;
      const [headerRow, ...bodyRows] = block.rows;
      return (
        <div key={index} className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                {headerRow.map((cell, ci) => (
                  <th key={ci} className={styles.th}>
                    {renderRuns(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={styles.td}>
                      {renderRuns(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'image': {
      // `src` is a CDN-relative path (e.g. /web_v2/legal/foo.png). CustomImage
      // (next/image + the site Speedsize loader) expects the path WITHOUT the
      // leading slash so its loader prefixes the asset host; absolute URLs pass
      // through. Matches how every other page renders imagery.
      return (
        <figure key={index} className={styles.figure}>
          <CustomImage
            className={
              block.borderless
                ? styles.styledImage
                : `${styles.styledImage} ${styles.styledImageFramed}`
            }
            src={
              block.src.startsWith('http')
                ? block.src
                : block.src.replace(/^\//, '')
            }
            alt={block.alt}
            width={block.width}
            height={block.height}
            sizes="(max-width: 768px) 90vw, 360px"
          />
        </figure>
      );
    }

    case 'divider':
      return <hr key={index} className={styles.hr} />;

    default:
      return null;
  }
}

function renderBlocks(
  blocks: LegalBlock[],
  headingIds: Map<LegalBlock, string>,
): React.ReactNode {
  const grouped = groupBlocks(blocks);

  return grouped.map((item, i) => {
    if (item.type === 'listGroup') {
      return (
        <ol key={`ol-${i}`} className={styles.orderedList}>
          {item.items.map((b, li) => {
            if (b.kind !== 'listItem') return null;
            return (
              <li key={li} className={styles.li}>
                {renderRuns(b.runs)}
              </li>
            );
          })}
        </ol>
      );
    }
    return renderBlock(item.block, i, headingIds);
  });
}

// ---------------------------------------------------------------------------
// Table of contents ("jump to section")
// ---------------------------------------------------------------------------

function TableOfContents({ sections }: { sections: Section[] }) {
  return (
    <details className={styles.toc}>
      <summary className={styles.tocSummary}>
        <span>Jump to section</span>
        <span aria-hidden="true" className={styles.toggleIcon}>
          <Plus fill="#A1A1A1" style={{ width: 16, height: 16 }} />
        </span>
      </summary>
      <ul className={styles.tocList}>
        {sections.map((s) => (
          <li key={s.id} data-sub={s.level === 2} className={styles.tocItem}>
            <a href={`#${s.id}`}>{s.title}</a>
          </li>
        ))}
      </ul>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Main exported component
// ---------------------------------------------------------------------------

type LegalDocumentProps = {
  doc: LegalDoc;
};

export function LegalDocument({ doc }: LegalDocumentProps) {
  const { sections, headingIds } = useMemo(
    () => collectSections(doc.blocks),
    [doc],
  );

  return (
    <PolicyPage>
      <PolicyContainer>
        {/* Eyebrow = static category label in the minimal treatment */}
        <Eyebrow>Legal</Eyebrow>
        {/* PageTitle = visible H1, satisfies heading hierarchy and SEO */}
        <PageTitle>{doc.title}</PageTitle>
        {/* Compact metadata row: last updated */}
        {doc.lastUpdated && (
          <div className={styles.metaRow}>
            <p className={styles.metaLabel}>Last updated: {doc.lastUpdated}</p>
          </div>
        )}
        <Divider />
        {sections.length > 1 && <TableOfContents sections={sections} />}
        <div className={styles.body}>{renderBlocks(doc.blocks, headingIds)}</div>
      </PolicyContainer>
      <BackToTop />
    </PolicyPage>
  );
}
